import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Flag, FlagOff, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { markForReview, clearReview, completeReview } from './reviewApi';

/**
 * Day 5 — Manual Review Card
 *
 * Displays current review status (not flagged | flagged) with optional note,
 * marker metadata, and manual Mark / Clear actions.
 * No notifications, no escalations, no workflow engine.
 *
 * Fails safe: renders null block contents if review data is absent.
 * Read-only on archived charts.
 */

const NOTE_MAX = 1000;

function formatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

export function ChartReviewCard({
  chart,
  participantId,
  canEdit,
  onChanged,
}) {
  const review = chart?.review || {};
  const flagged = review.status === 'flagged';
  const isActive = chart?.status === 'active';
  const editable = canEdit && isActive;

  const [markOpen, setMarkOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [busy, setBusy] = useState(false);

  const openMark = () => { setNote(''); setMarkOpen(true); };
  const openComplete = () => { setOutcomeNote(''); setCompleteOpen(true); };

  const submitMark = async () => {
    const trimmed = (note || '').trim();
    if (trimmed.length > NOTE_MAX) {
      toast.error(`Note must be ≤${NOTE_MAX} characters`);
      return;
    }
    try {
      setBusy(true);
      await markForReview(participantId, chart.id, trimmed);
      toast.success('Chart marked for review');
      setMarkOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to mark for review');
    } finally { setBusy(false); }
  };

  const submitClear = async () => {
    try {
      setBusy(true);
      await clearReview(participantId, chart.id);
      toast.success('Review cleared');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to clear review');
    } finally { setBusy(false); }
  };

  const submitComplete = async () => {
    const trimmed = (outcomeNote || '').trim();
    if (trimmed.length > NOTE_MAX) {
      toast.error(`Outcome note must be ≤${NOTE_MAX} characters`);
      return;
    }
    try {
      setBusy(true);
      await completeReview(participantId, chart.id, trimmed);
      toast.success('Review completed');
      setCompleteOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to complete review');
    } finally { setBusy(false); }
  };

  return (
    <Card
      className={`border ${flagged ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`}
      data-testid="chart-review-card"
    >
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              <Flag className="w-3 h-3" /> Review
            </div>
            {flagged ? (
              <Badge variant="outline" className="text-[10px] bg-amber-100 border-amber-400 text-amber-900" data-testid="review-status-flagged">
                Flagged for review
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600" data-testid="review-status-clear">
                Not flagged
              </Badge>
            )}
          </div>
          {editable && (
            <div className="flex items-center gap-1.5">
              {flagged ? (
                <>
                  <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openComplete} disabled={busy} data-testid="complete-review-btn">
                    <CheckCircle2 className="w-3 h-3 mr-1" />Mark as Reviewed
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={submitClear} disabled={busy} data-testid="clear-review-btn">
                    <FlagOff className="w-3 h-3 mr-1" />Clear review
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openMark} data-testid="mark-review-btn">
                  <Flag className="w-3 h-3 mr-1" />Mark for review
                </Button>
              )}
            </div>
          )}
        </div>

        {flagged ? (
          <div className="space-y-1 pt-1">
            {review.review_note && (
              <div
                className="text-xs bg-white/60 border border-amber-200 rounded p-2 whitespace-pre-wrap"
                data-testid="review-note"
              >{review.review_note}</div>
            )}
            <div className="text-[11px] text-muted-foreground" data-testid="review-marked-at">
              Marked at: <span className="font-medium text-foreground">{formatDateTime(review.review_marked_at)}</span>
            </div>
          </div>
        ) : (
          !isActive ? (
            <p className="text-xs text-muted-foreground" data-testid="review-archived-note">
              This chart is archived — review actions are disabled.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="review-empty">
              This chart has not been flagged for review.
            </p>
          )
        )}

        {/* Day 7 — last completed review (shown whenever completion metadata exists, even on archived charts) */}
        {chart?.review_completed && (
          <div className="mt-2 pt-2 border-t space-y-1" data-testid="review-completed-block">
            <div className="flex items-center gap-1.5 text-[11px]">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              <span className="text-muted-foreground">Last reviewed:</span>
              <span className="font-medium" data-testid="review-completed-at">{formatDateTime(chart.review_completed.completed_at)}</span>
              <a
                href="/charts/review-outcomes"
                className="ml-auto text-[10px] text-emerald-700 hover:underline"
                data-testid="review-see-all-link"
              >See all outcomes →</a>
            </div>
            {chart.review_completed.outcome_note && (
              <div
                className="text-xs bg-emerald-50/60 border border-emerald-200 rounded p-2 whitespace-pre-wrap"
                data-testid="review-outcome-note"
              >
                <span className="text-[10px] uppercase tracking-wide text-emerald-700 block mb-0.5">Outcome</span>
                {chart.review_completed.outcome_note}
              </div>
            )}
          </div>
        )}

        {/* Day 7 — tiny, ordered review history (reuses existing fields, no new collection) */}
        {(review.review_marked_at || chart?.review_completed?.completed_at || review.review_cleared_at) && (
          <div className="mt-2 pt-2 border-t" data-testid="review-history">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Review history</div>
            <ul className="text-[11px] space-y-0.5 text-muted-foreground">
              {review.review_marked_at && (
                <li data-testid="review-history-marked">
                  <span className="text-amber-700">●</span> Marked on <span className="text-foreground">{formatDateTime(review.review_marked_at)}</span>
                </li>
              )}
              {chart?.review_completed?.completed_at && (
                <li data-testid="review-history-completed">
                  <span className="text-emerald-700">●</span> Reviewed on <span className="text-foreground">{formatDateTime(chart.review_completed.completed_at)}</span>
                </li>
              )}
              {review.review_cleared_at && !chart?.review_completed?.completed_at && (
                <li data-testid="review-history-cleared">
                  <span className="text-slate-500">●</span> Cleared on <span className="text-foreground">{formatDateTime(review.review_cleared_at)}</span>
                </li>
              )}
            </ul>
          </div>
        )}

        <Dialog open={markOpen} onOpenChange={setMarkOpen}>
          <DialogContent className="max-w-md" data-testid="mark-review-dialog">
            <DialogHeader>
              <DialogTitle>Mark for review</DialogTitle>
              <DialogDescription>
                Flag this chart so another clinician knows to take a look. No notifications are sent.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Review note (optional)</label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                maxLength={NOTE_MAX + 50}
                placeholder="e.g. Observed hard stool pattern and recent pain/discomfort — please review."
                data-testid="review-note-input"
              />
              <div className="text-[10px] text-muted-foreground text-right">{note.length}/{NOTE_MAX}</div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setMarkOpen(false)} data-testid="mark-review-cancel">Cancel</Button>
              <Button size="sm" onClick={submitMark} disabled={busy} data-testid="mark-review-confirm">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Flag className="w-3.5 h-3.5 mr-1" />}
                Mark for review
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
          <DialogContent className="max-w-md" data-testid="complete-review-dialog">
            <DialogHeader>
              <DialogTitle>Mark as reviewed</DialogTitle>
              <DialogDescription>
                Confirm that this chart has been reviewed. Add an outcome note if useful.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Outcome note (optional)</label>
              <Textarea
                value={outcomeNote}
                onChange={(e) => setOutcomeNote(e.target.value)}
                rows={4}
                maxLength={NOTE_MAX + 50}
                placeholder="e.g. Reviewed with clinician; no change needed, continue monitoring."
                data-testid="review-outcome-input"
              />
              <div className="text-[10px] text-muted-foreground text-right">{outcomeNote.length}/{NOTE_MAX}</div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setCompleteOpen(false)} data-testid="complete-review-cancel">Cancel</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitComplete} disabled={busy} data-testid="complete-review-confirm">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                Confirm review
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default ChartReviewCard;
